import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router';
import { RefreshCw, Trash2 } from 'lucide-react';
import type { TeamInput } from '@airmail/engine';
import { useCompetition, useSync, useTurnier } from '@/store/TournamentProvider';
import { adminDeleteRegistration, adminRegistrations, type AdminRegistration, type RegistrationConfig } from '@/store/registration';
import { Button, Dialog, cx } from '@/ui';
import { confirmDialog } from '@/ui/dialogs';
import { fmtTime } from '@/lib/format';
import { locale, useT } from '@/i18n';

/** Teams → „Anmeldungen“: Liste vom Server, Übernahme als Teams (docs/15) */
const norm = (p: { firstName: string; lastName: string }) => `${p.firstName} ${p.lastName}`.trim().toLowerCase();

export function RegistrationsDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const t = useT();
  const { tournament, dispatch, patchSyncCfg } = useTurnier();
  const sync = useSync();
  const comp = useCompetition();
  const [cfg, setCfg] = useState<RegistrationConfig | null>(null);
  const [list, setList] = useState<AdminRegistration[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  // Übernommen = auf diesem Gerät gemerkt ODER es gibt schon ein Team mit genau diesen Spielern (anderes Gerät)
  const teamKeys = useMemo(() => new Set(comp.teams.map((t) => t.players.map(norm).sort().join('|'))), [comp.teams]);
  const imported = new Set([...(sync.cfg?.importedRegs ?? []), ...(list ?? []).filter((r) => teamKeys.has(r.players.map(norm).sort().join('|'))).map((r) => r.id)]);

  /*
   * Die Auswahl wird nur beim ersten Laden vorbelegt (alles Neue angehakt).
   * Vorher lief `load` mit jedem Abgleich neu (die Sync-Konfiguration ist dann
   * ein neues Objekt) und hakte alle 8 Sekunden wieder an, was man gerade
   * bewusst abgewählt hatte. Später Eingetroffenes kommt angehakt dazu.
   */
  const syncRef = useRef(sync.cfg);
  syncRef.current = sync.cfg;
  const teamKeysRef = useRef(teamKeys);
  teamKeysRef.current = teamKeys;
  const gesehen = useRef<Set<string> | null>(null);
  const load = useCallback(async () => {
    const s = syncRef.current;
    if (!s) return;
    try {
      const r = await adminRegistrations(s);
      setCfg(r.config);
      setList(r.registrations);
      setError(null);
      const known = new Set(s.importedRegs ?? []);
      const neu = (x: AdminRegistration) => !known.has(x.id) && !teamKeysRef.current.has(x.players.map(norm).sort().join('|'));
      const vorher = gesehen.current;
      if (!vorher) setSelected(new Set(r.registrations.filter(neu).map((x) => x.id)));
      else {
        const ids = new Set(r.registrations.map((x) => x.id));
        setSelected((sel) => new Set([...[...sel].filter((id) => ids.has(id)), ...r.registrations.filter((x) => !vorher.has(x.id) && neu(x)).map((x) => x.id)]));
      }
      gesehen.current = new Set(r.registrations.map((x) => x.id));
    } catch (e) {
      setError((e as Error).message);
    }
  }, []);

  useEffect(() => {
    if (open) void load();
    // Beim nächsten Öffnen wieder frisch vorbelegen
    else gesehen.current = null;
  }, [open, load, tournament.id]);

  const toTeam = (r: AdminRegistration): TeamInput => {
    const team: TeamInput = { players: r.players.map((p) => ({ firstName: p.firstName, lastName: p.lastName })), paid: false };
    if (r.teamName) team.name = r.teamName;
    if (r.club) team.club = r.club;
    /*
     * Keine Kontaktdaten in die Team-Notiz. Die Notiz steht im Aktionsprotokoll,
     * und das geht über den Zuschauer-Link an jeden, der ihn hat — E-Mail und
     * Telefon der Teilnehmer hätten so auf dem Beamer und in der
     * WhatsApp-Gruppe gestanden. Die Leitung sieht sie weiterhin hier in der
     * Anmeldeliste; ins Turnier kommt nur, was der Teilnehmer als Bemerkung
     * geschrieben hat.
     */
    if (r.note) team.note = r.note;
    // Antworten auf die Zusatzfragen mit Beschriftung — die Teams-Seite zeigt sie als Spalten (docs/15 §6)
    const answers = antwortenVon(r);
    if (answers.length) team.answers = answers;
    return team;
  };

  const antwortenVon = (r: AdminRegistration): NonNullable<TeamInput['answers']> => {
    const out: NonNullable<TeamInput['answers']> = [];
    for (const f of cfg?.fields ?? []) {
      const v = r.answers?.[f.id];
      if (v === undefined || v === '' || v === false) continue;
      out.push({ id: f.id, label: f.label, value: v });
    }
    return out;
  };
  const antwortText = (r: AdminRegistration): string =>
    antwortenVon(r)
      .map((a) => (a.value === true ? a.label : `${a.label}: ${a.value}`))
      .join(' · ');

  const take = async () => {
    if (!list || !sync.cfg) return;
    setBusy(true);
    const done: string[] = [];
    for (const r of list) {
      if (!selected.has(r.id) || imported.has(r.id)) continue;
      // Über dem Limit der Anmeldung: auf die Warteliste — rückt nach, sobald jemand abgemeldet wird
      const warteliste = cfg?.maxTeams !== undefined && r.position > cfg.maxTeams;
      const res = dispatch({ type: 'team.add', competitionId: comp.id, team: { ...toTeam(r), ...(warteliste ? { waitlist: true } : {}) } });
      if (res.ok) done.push(r.id);
    }
    if (done.length) patchSyncCfg({ importedRegs: [...new Set([...(sync.cfg.importedRegs ?? []), ...done])] });
    setBusy(false);
    if (done.length) onClose();
  };

  const remove = async (r: AdminRegistration) => {
    if (!sync.cfg || !(await confirmDialog(t('Anmeldung „{name}“ vom Server löschen?', { name: label(r) }), { ok: t('Löschen'), danger: true }))) return;
    try {
      await adminDeleteRegistration(sync.cfg, r.id);
      await load();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const label = (r: AdminRegistration) => r.teamName ?? r.players.map((p) => `${p.firstName} ${p.lastName}`.trim()).join(' / ');
  const openCount = list?.filter((r) => !imported.has(r.id)).length ?? 0;
  const selectable = list?.filter((r) => !imported.has(r.id) && selected.has(r.id)).length ?? 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t('Anmeldungen')}
      width="max-w-3xl"
      footer={
        <>
          <span className="flex-1 text-xs text-dim">{list ? t('{n} Anmeldungen · {m} noch nicht übernommen', { n: list.length, m: openCount }) : ''}</span>
          <Button variant="ghost" onClick={() => void load()}>
            <RefreshCw size={14} /> {t('Aktualisieren')}
          </Button>
          <Button variant="ghost" onClick={onClose}>
            {t('Schließen')}
          </Button>
          <Button variant="primary" onClick={take} disabled={busy || selectable === 0}>
            {t('{n} als Teams übernehmen', { n: selectable })}
          </Button>
        </>
      }
    >
      {!sync.cfg && <div className="text-sm text-muted">{t('Anmeldungen brauchen Cloud-Sync — unter Einstellungen aktivieren.')}</div>}
      {error && <div className="text-err text-sm mb-3">{error}</div>}
      {sync.cfg && !cfg && list && (
        <div className="text-sm text-muted">
          {t('Die Anmeldung ist noch nicht eingerichtet —')}{' '}
          <Link to={`/t/${tournament.id}/einstellungen`} className="text-blue" onClick={onClose}>
            {t('in den Einstellungen einschalten')}
          </Link>
          .
        </div>
      )}
      {list && list.length === 0 && cfg && <div className="text-sm text-dim">{t('Noch keine Anmeldungen. Link teilen: Einstellungen → Anmeldung.')}</div>}
      {list && list.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b border-line">
                <th className="w-8"></th>
                <th className="text-left px-2 py-1.5 label">#</th>
                <th className="text-left px-2 py-1.5 label">{t('Team')}</th>
                <th className="text-left px-2 py-1.5 label">{t('Spieler')}</th>
                <th className="text-left px-2 py-1.5 label">{t('Verein / Ort')}</th>
                <th className="text-left px-2 py-1.5 label">{t('Kontakt')}</th>
                {cfg?.fields && cfg.fields.length > 0 && <th className="text-left px-2 py-1.5 label">{t('Antworten')}</th>}
                <th className="text-left px-2 py-1.5 label">{t('Eingang')}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {list.map((r) => {
                const isImported = imported.has(r.id);
                const waitlist = cfg?.maxTeams !== undefined && r.position > cfg.maxTeams;
                return (
                  <tr key={r.id} className={cx('border-b border-line2', isImported && 'opacity-50')}>
                    <td className="px-2 py-1.5">
                      <input
                        type="checkbox"
                        disabled={isImported}
                        checked={!isImported && selected.has(r.id)}
                        onChange={(e) => {
                          const next = new Set(selected);
                          if (e.target.checked) next.add(r.id);
                          else next.delete(r.id);
                          setSelected(next);
                        }}
                      />
                    </td>
                    <td className="px-2 py-1.5 font-mono text-xs text-dim">{r.position}</td>
                    <td className="px-2 py-1.5 font-bold">
                      {r.teamName ?? <span className="text-dim font-normal">–</span>}
                      {isImported && <span className="label text-live ml-2">{t('übernommen')}</span>}
                      {!isImported && waitlist && <span className="label text-warn ml-2">{t('Warteliste')}</span>}
                    </td>
                    <td className="px-2 py-1.5 text-muted">{r.players.map((p) => `${p.firstName} ${p.lastName}`.trim()).join(', ')}</td>
                    <td className="px-2 py-1.5 text-muted">{r.club ?? ''}</td>
                    <td className="px-2 py-1.5 text-xs text-muted">
                      {[r.email, r.phone].filter(Boolean).join(' · ')}
                      {r.note && <div className="text-dim">{r.note}</div>}
                    </td>
                    {cfg?.fields && cfg.fields.length > 0 && <td className="px-2 py-1.5 text-xs text-muted">{antwortText(r) || <span className="text-dim">–</span>}</td>}
                    <td className="px-2 py-1.5 text-xs text-dim whitespace-nowrap">
                      {new Date(r.createdAt).toLocaleDateString(locale(), { day: '2-digit', month: '2-digit' })} {fmtTime(r.createdAt)}
                    </td>
                    <td className="px-2 py-1.5">
                      <button className="text-dim hover:text-err cursor-pointer" title={t('Anmeldung löschen')} onClick={() => void remove(r)}>
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Dialog>
  );
}
